import React, { useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    CircularProgress,
    Grid,
    Button,
    InputAdornment
} from '@mui/material';
import { adminApi } from '../../services/api';
import CodeOwnerDetails from './CodeOwnerDetails';
import EmailHistory from './EmailHistory';

const CodeOwnerTab = () => {
    const [searchParams, setSearchParams] = useState({
        email: '',
        ownerId: '',
        code: ''
    });
    const [loading, setLoading] = useState(false);
    const [ownerData, setOwnerData] = useState(null);
    const [error, setError] = useState(null);

    const handleSearch = async (field) => {
        const value = searchParams[field];
        if (!value) return;

        try {
            setLoading(true);
            setError(null);
            const response = await adminApi.searchCodeOwner({ [field]: value });
            setOwnerData(response.data);
        } catch (err) {
            setError(err.message || 'Failed to fetch code owner data');
            setOwnerData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        // Clear other fields when typing in one field
        setSearchParams({
            email: field === 'email' ? value : '',
            ownerId: field === 'ownerId' ? value : '',
            code: field === 'code' ? value : ''
        });
    };

    return (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Email"
                        value={searchParams.email}
                        onChange={handleInputChange('email')}
                        placeholder="Search by exact email"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleSearch('email')}
                                        disabled={!searchParams.email || loading}
                                        sx={{ minWidth: '40px', px: 1 }}
                                    >
                                        Go
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Owner ID"
                        value={searchParams.ownerId}
                        onChange={handleInputChange('ownerId')}
                        placeholder="Search by exact owner ID"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleSearch('ownerId')}
                                        disabled={!searchParams.ownerId || loading}
                                        sx={{ minWidth: '40px', px: 1 }}
                                    >
                                        Go
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth
                        label="Code"
                        value={searchParams.code}
                        onChange={handleInputChange('code')}
                        placeholder="Search by exact code (e.g., SG0905)"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleSearch('code')}
                                        disabled={!searchParams.code || loading}
                                        sx={{ minWidth: '40px', px: 1 }}
                                    >
                                        Go
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
            </Grid>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Typography color="error" sx={{ my: 2 }}>
                    {error}
                </Typography>
            )}

            {ownerData && (
                <Box>
                    <CodeOwnerDetails 
                        ownerData={ownerData} 
                        onDeleted={() => {
                            setOwnerData(null);
                            setSearchParams({
                                email: '',
                                ownerId: '',
                                code: ''
                            });
                        }}
                    />
                    <EmailHistory emailHistory={ownerData.email_history} />
                </Box>
            )}
        </Box>
    );
};

export default CodeOwnerTab;
